import { DetailedAddress } from '@/lib/types'
import { FormikProps } from 'formik'

export namespace Question {
  export namespace Answer {
    export type Option = {
      id: string
      label: string
      disclaimer?: string
    }
  }

  export type DateRangeOptions = {
    minStart: number
    maxStart: number
    minRange: number
    maxRange: number
  }

  export type DateOptions = {
    min: number
    max: number
  }

  export type FreeText = {
    type: 'free-text'
    key: string
    title: string
    optional: boolean
    multiline: boolean
  }

  export type Address = {
    type: 'address'
    key: string
    title: string
    optional: boolean
    onPickAddress: (
      address: DetailedAddress,
      formikProps: FormikProps<any>,
    ) => void
  }

  /*
   * An updated, more correct type for yes no questions. Apparently, I used strings 'Yes' and 'No' when I started out,
   * in an amateur fashion :facepalm:
   * */
  export type YesNoBoolean = {
    type: 'yes-no-boolean'
    key: string
    title: string
    labels: [string, string]
    disclaimer?: [string | undefined, string | undefined]
    optional: boolean
  }

  export type SingleChoice = {
    type: 'single-choice'
    key: string
    title: string
    options: Answer.Option[]
    optional: boolean
  }

  export type MultipleChoice = {
    type: 'multiple-choice'
    key: string
    title: string
    options: Answer.Option[]
    optional: boolean
  }

  export type YesNo = {
    type: 'yes-no'
    key: string
    title: string
    optional: boolean
  }

  export type Date = {
    type: 'date'
    key: string
    title: string
    options?: DateOptions
    optional: boolean
  }

  export type DateRange = {
    type: 'date-range'
    startKey: string
    endKey: string
    startTitle: string
    endTitle: string
    options?: DateRangeOptions
    optional: boolean
  }

  export type Duration = {
    type: 'duration'
    title: string
    yearKey: string
    monthKey: string
    optional: boolean
  }

  export function freeText(
    key: string,
    title: string,
    optional: boolean = false,
    multiline: boolean = true,
  ): Question {
    return {
      type: 'free-text',
      key,
      title,
      optional,
      multiline,
    }
  }

  export function address(
    key: string,
    title: string,
    onPickAddress: (
      address: DetailedAddress,
      formikProps: FormikProps<any>,
    ) => void,
    optional: boolean = false,
  ): Question {
    return {
      type: 'address',
      key,
      title,
      onPickAddress,
      optional,
    }
  }

  export function singleChoice(
    key: string,
    title: string,
    options: Answer.Option[],
    optional: boolean = false,
  ): Question {
    return {
      type: 'single-choice',
      key,
      title,
      options,
      optional,
    }
  }

  export function multipleChoice(
    key: string,
    title: string,
    options: Answer.Option[],
    optional: boolean = false,
  ): Question {
    return {
      type: 'multiple-choice',
      key,
      title,
      options,
      optional,
    }
  }

  export function yesNo(
    key: string,
    title: string,
    optional: boolean = false,
  ): Question {
    return {
      type: 'yes-no',
      key,
      title,
      optional,
    }
  }

  export function yesNoBoolean(
    key: string,
    title: string,
    labels: [string, string] = ['Yes', 'No'],
    optional: boolean = false,
    disclaimer?: [string | undefined, string | undefined],
  ): Question {
    return {
      type: 'yes-no-boolean',
      key,
      title,
      labels,
      optional,
      disclaimer,
    }
  }

  export function date(
    key: string,
    title: string,
    optional: boolean = false,
    options?: DateOptions,
  ): Question {
    return {
      type: 'date',
      key,
      title,
      options,
      optional,
    }
  }

  export function dateRange(
    startKey: string,
    endKey: string,
    startTitle: string,
    endTitle: string,
    optional: boolean = false,
    options?: DateRangeOptions,
  ): Question {
    return {
      type: 'date-range',
      startKey,
      endKey,
      startTitle,
      endTitle,
      options,
      optional,
    }
  }

  export function duration(
    yearKey: string,
    monthKey: string,
    title: string,
    optional: boolean = false,
  ): Question {
    return {
      type: 'duration',
      yearKey,
      monthKey,
      optional,
      title,
    }
  }
}

export type Question =
  | Question.FreeText
  | Question.Address
  | Question.SingleChoice
  | Question.MultipleChoice
  | Question.YesNo
  | Question.Date
  | Question.DateRange
  | Question.Duration
  | Question.YesNoBoolean

export type QuestionnairePage =
  | {
      questions: Question[]
    }
  | 'personal-information'
  | 'upload-supporting-document'
  | 'call-arrangement'

export interface Questionnaire {
  pages: QuestionnairePage[]
}
